import { createSlice } from '@reduxjs/toolkit';
import Cookies from 'universal-cookie';

const cookies = new Cookies();

// Helper to get all merchant sessions from cookies
const getInitialSessions = () => {
  const allCookies = cookies.getAll();
  const sessions = {};
  Object.keys(allCookies).forEach(key => {
    if (key.startsWith('customerToken_')) {
      const merchantId = key.replace('customerToken_', '');
      sessions[merchantId] = {
        token: allCookies[key],
        user: allCookies[`customerUser_${merchantId}`] ? JSON.parse(decodeURIComponent(allCookies[`customerUser_${merchantId}`])) : null
      };
    }
  });
  return sessions;
};

const initialState = {
  user: null, // Admin/Merchant user
  token: cookies.get('authToken') || null,
  currentMerchantId: null, // Track current restaurant context
  customerSessions: getInitialSessions(),
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (state, action) => {
      const { user, token } = action.payload;
      state.user = user;
      state.token = token;
      cookies.set('authToken', token, { path: '/' });
      if (user?.user_id) {
        cookies.set('userId', user.user_id, { path: '/' });
      }
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      cookies.remove('authToken', { path: '/' });
      cookies.remove('userId', { path: '/' });
    },
    setCurrentMerchantId: (state, action) => {
      state.currentMerchantId = action.payload;
    },
    setCustomerCredentials: (state, action) => {
      const { user, token, merchantId, altMerchantId } = action.payload;
      const mId = merchantId || state.currentMerchantId;
      if (!mId) return;

      const session = { user, token };
      state.customerSessions[mId] = session;
      cookies.set(`customerToken_${mId}`, token, { path: '/' });
      cookies.set(`customerUser_${mId}`, encodeURIComponent(JSON.stringify(user)), { path: '/' });
      
      // Also save for an alternative ID if provided (e.g. numeric ID vs slug)
      if (altMerchantId && altMerchantId !== mId) {
        state.customerSessions[altMerchantId] = session;
        cookies.set(`customerToken_${altMerchantId}`, token, { path: '/' });
        cookies.set(`customerUser_${altMerchantId}`, encodeURIComponent(JSON.stringify(user)), { path: ' / ' });
      }
    },
    customerLogout: (state, action) => {
      const mId = action.payload || state.currentMerchantId;
      if (!mId) return;

      delete state.customerSessions[mId];
      cookies.remove(`customerToken_${mId}`, { path: '/' });
      cookies.remove(`customerUser_${mId}`, { path: '/' });
    },
  },
});

export const { setCredentials, logout, setCurrentMerchantId, setCustomerCredentials, customerLogout } = authSlice.actions;

export default authSlice.reducer;

export const selectCurrentUser = (state) => state.auth.user;
export const selectCurrentToken = (state) => state.auth.token;
export const selectCurrentMerchantId = (state) => state.auth.currentMerchantId;
export const selectCustomerUser = (state) => {
    const mId = state.auth.currentMerchantId;
    return mId ? state.auth.customerSessions[mId]?.user : null;
};
export const selectCustomerToken = (state) => {
    const mId = state.auth.currentMerchantId;
    return mId ? state.auth.customerSessions[mId]?.token : null;
};
export const selectMerchantToken = (state, merchantId) => state.auth.customerSessions[merchantId]?.token;
